import { defineHttpJsonApi } from "@flow/lib/http_json_api";
import { Serializers } from "@flow/common/types";
import * as Safe from "safe-portals";

export const safeNewlineSeparatedStringArrayEww: Safe.Type<string[]> = {
  container: "none",
  description: () => "NewlineSeparatedStringList",
  read: function (o): string[] {
    return typeof o === "string" ? o.split("\n") : Safe.validationError("string", o);
  },
  write: (t) => t.join("\n"),
};

export const idDonationMetadata = Safe.obj({
  amount: Safe.float,
  amount_charged: Safe.float,
  currency: Safe.str,
  boost_amount: Safe.optional(Safe.float),
  card_type: Safe.raw,
  payment_processor: Safe.str,
  transaction_id: Safe.optional(Safe.str), // null for action_type=regular_donate
  customer_id: Safe.optional(Safe.str),
  description: Safe.str,
  tax_deductible: Safe.optional(Safe.bool), // ?? is this right ??
  status: Safe.oneOf("success"), // what other statuses are valid?
});

export const CanonicalQuestionSerializer = Safe.obj({
  id: Safe.int,
  question: Safe.str,
  // grid, file and text_block aren't supported canonical question types
  qtype: Safe.oneOf("check_box", "check_boxes", "radio_buttons", "ranking", "select", "text", "text_area"),
  options: Safe.optional(safeNewlineSeparatedStringArrayEww),
});

export const QuestionAnswerSerializer = Safe.obj({
  question: Safe.obj({
    text: Safe.str,
    // This means: 'check_box' | 'check_boxes' | ...
    qtype: Safe.oneOf("check_box", "check_boxes", "radio_buttons", "ranking", "select", "text", "text_area"),
    external_canonical_question_id: Safe.optional(Safe.str),
  }),
  answer: Safe.raw,
});

export type QuestionAnswerType = Safe.TypeIn<typeof QuestionAnswerSerializer>;

export const idApiGetRandomCommonQuestion = defineHttpJsonApi(
  "get",
  "/api/canonical_questions/random",
  Safe.obj({
    api_token: Safe.str,
    email: Safe.str,
  }),
  Safe.optional(CanonicalQuestionSerializer)
);

export const idApiGetConsentTexts = defineHttpJsonApi(
  "get",
  "/api/consents/consent_texts",
  // arguments
  Safe.obj({
    api_token: Safe.str,
  }),
  // response
  Safe.array(
    Safe.obj({
      public_id: Safe.str,
      consent_short_text: Safe.str,
      full_legal_text_link: Safe.str,
      created_at: Safe.dateIso,
      updated_at: Safe.dateIso,
    })
  )
);

export const idApiGetMemberDetails = defineHttpJsonApi(
  "post",
  "/api/member/details",
  // arguments
  Safe.obj({
    api_token: Safe.str,
    guid: Safe.str, // ID does not use valid uuids, so not Safe.uuid
    load_current_consents: Safe.bool,
  }),
  // response
  // XXX ID's response is not very safe-portals friendly. it can
  // respond with {}, or will the full structure. the best
  // we can do to represent this is partial_obj, which makes
  // all properties optional.
  Safe.partial_obj({
    // it actually returns many other things that we don't care about yet
    id: Safe.int,
    guid: Safe.str, // XXX ID guids are not always valid uuids, so don't use Safe.uuid
    first_name: Safe.str,
    middle_names: Safe.str,
    last_name: Safe.str,
    email: Safe.str,
    address: Safe.obj({
      line1: Safe.optional(Safe.str),
      line2: Safe.optional(Safe.str),
      town: Safe.optional(Safe.str),
      postcode: Safe.optional(Safe.str),
      country: Safe.optional(Safe.str),
    }),
    phone_number: Safe.optional(Safe.str),
    consents: Safe.optional(
      Safe.array(
        Safe.obj({
          public_id: Safe.str,
          consent_level: Serializers.consentLevel,
          consent_created_at: Safe.dateIso,
          consent_method: Safe.optional(Serializers.consentMethod),
          consent_method_option: Safe.optional(Safe.str),
        })
      )
    ),
  })
);

const createDonationSerializer = Safe.obj({
  api_token: Safe.str,
  first_name: Safe.str,
  last_name: Safe.str,
  created_at: Safe.dateIso,
  amount: Safe.float,
  metadata: Safe.optional(Safe.raw),
  source: Safe.str,
  action_name: Safe.str,
  action_type: Safe.str,
  action_technical_type: Safe.str,
  action_external_id: Safe.str,
  action_source: Safe.str, // these become source.{source,medium,campaign} on the member_action->source object in ID
  action_medium: Safe.str,
  action_campaign: Safe.str,
  medium: Safe.str,
  postcode: Safe.str,
  country: Safe.str,
  email: Safe.str,
  external_id: Safe.str,
  campaign_id: Safe.optional(Safe.str),
});

export const idApiOneOffDonationCreate = defineHttpJsonApi(
  "post",
  "/api/contributions",
  // arguments
  createDonationSerializer,
  // response - anything is ok
  Safe.raw
);

/** Regular donation messages are one off donation messages + extra bits */
const createRegularDonationSerializer = Safe.combine(
  createDonationSerializer,
  Safe.obj({
    regular_donation_external_id: Safe.str,
    regular_donation_system: Safe.str,
    regular_donation_frequency: Safe.str,
    regular_donation_started_at: Safe.optional(Safe.dateIso),
    regular_donation_source: Safe.str,
  })
);

export const idApiRegularDonationCreate = defineHttpJsonApi(
  "post",
  "/api/contributions",
  // arguments
  createRegularDonationSerializer,
  // response - anything is ok
  Safe.raw
);

/** A seriallizer for sending messages to the /api/member_actions/create endpoint */
const createMemberActionsSerializer = Safe.obj({
  api_token: Safe.str,
  cons_hash: Safe.obj({
    emails: Safe.array(Safe.obj({ email: Safe.str })),
    firstname: Safe.str,
    lastname: Safe.str,
    addresses: Safe.array(
      Safe.obj({
        line1: Safe.str,
        line2: Safe.str,
        town: Safe.str,
        postcode: Safe.str,
        country: Safe.str,
        state: Safe.str,
      })
    ),
  }),
  // ID pushes these key-value pairs into its member_action_data table.
  // For donations, metadata is described by idDonationMetadata (see above)
  // but we leave it untyped here to permit other metadata schemas
  //
  metadata: Safe.optional(Safe.raw),
  survey_responses: Safe.optional(Safe.array(QuestionAnswerSerializer)),
  action_technical_type: Safe.str,
  external_id: Safe.str,
  action_name: Safe.str,
  action_public_name: Safe.str,
  action_type: Safe.str,
  action_description: Safe.str,
  create_dt: Safe.dateIso,
  source: Safe.partial_obj({
    source: Safe.str,
    medium: Safe.str,
    campaign: Safe.str,
  }),
  consents: Safe.array(
    Safe.obj({
      public_id: Safe.str,
      consent_level: Serializers.consentLevel,
      consent_method: Safe.optional(Serializers.consentMethod),
      consent_method_option: Safe.optional(Safe.str),
    })
  ),
  opt_in: Safe.optional(Safe.bool),
  // campaign_id is not handled by identity yet
  // it will be used to link the action to a campaign
  campaign_id: Safe.optional(Safe.str),
});

/**
 * Definition of an identity API endpoint.
 *
 * We define it like this so that code that calls ID can be safely refactored,
 * and we are informed by the compiler when we violate the API definition.
 */
export const idApiMemberActionsCreate = defineHttpJsonApi(
  "post",
  "/api/member_actions/create",
  // arguments
  createMemberActionsSerializer,
  // response (raw means accept anything)
  Safe.raw
);

// Returns a paged list of campaigns for us to choose from
// Currently only the 1st page is available to flow
export const idApiGetCampaigns = defineHttpJsonApi(
  "get",
  "/api/campaigns/search",
  // arguments
  // Partial because exclude_controlshift is an optional parameter
  Safe.partial_obj({
    api_token: Safe.str,
    query: Safe.optional(Safe.str), // This is used to filter the results that ID sends back to the most relevant ones
    exclude_controlshift: Safe.raw, // exclude_controlshift is an optional param, if it is present, no matter the value, ID will exclude controlshift campaigns
  }),
  // response
  Safe.obj({
    next_page: Safe.int,
    campaigns: Safe.array(Safe.obj({ id: Safe.int, name: Safe.str })),
  })
);

// Creates a new campaign in ID and returns the details for us to use here
export const idApiCreateCampaign = defineHttpJsonApi(
  "post",
  "/api/campaigns/create",
  // arguments
  Safe.obj({
    api_token: Safe.str,
    name: Safe.str,
    description: Safe.str,
  }),
  // response
  Safe.obj({ id: Safe.int, name: Safe.str })
);
